import React from 'react';
import PropTypes from 'prop-types';
import {IconDown, IconUp} from './Icons';
import parse from 'html-react-parser';
import {getStyle} from './GelBand.utils';
import {
  GelDragDropContext,
  splitIDs,
  getCellTypeFromID,
  getNextClipValue,
  getPreviousClipValue,
} from '../GelDragDrop.utils';
import {GEL_TYPE, GEL_DRAG_DROP, CELL_TYPE} from '../GelDragDrop.constants';
import {Dropdown} from '@learningblocks/shared-components';
import '../style.scss';

const KEY = {
  DOWN_ARROW: 40,
  UP_ARROW: 38,
};

const bandRef = React.createRef();

export function GelBand(props) {
  const {
    state,
    clipValues,
    onStripChange,
    onDropDownChange,
    onGelBandClick,
  } = React.useContext(GelDragDropContext);
  const style = getStyle({state, props});

  const setDraggable = () =>
    props.options === null && props.gelType === GEL_TYPE.DRAGGABLE;

  const onDragStart = (event) => {
    event.dataTransfer.setData(GEL_DRAG_DROP.GEL_ID, props.gelId);
    event.dataTransfer.setDragImage(
      event.target,
      event.target.clientWidth / 2,
      0
    );
  };

  const {cellType} = getCellTypeFromID(props.gelId);

  const onUpIconChange = () => {
    const [colId, prevCellId, gelId] = splitIDs(props.gelId);
    if (
      cellType === CELL_TYPE.TRASH_CAN ||
      clipValues[clipValues.length - 1] === props.value
    ) {
      onStripChange({
        colId,
        prevCellId,
        currentCellId: prevCellId - 1,
        gelId,
        value: cellType === CELL_TYPE.TRASH_CAN ? clipValues[0] : 0,
      });
    } else {
      const value = getNextClipValue(clipValues, props.value);
      onStripChange({
        colId,
        prevCellId,
        currentCellId: prevCellId,
        gelId,
        value,
      });
    }
  };

  const onDownIconChange = () => {
    const [colId, prevCellId, gelId] = splitIDs(props.gelId);
    if (cellType === CELL_TYPE.GEL_HOLDER || clipValues[0] === props.value) {
      props.gelType === GEL_TYPE.DRAGGABLE &&
        onStripChange({
          colId,
          prevCellId,
          currentCellId: prevCellId + 1,
          gelId,
          value:
            cellType === CELL_TYPE.GEL_HOLDER
              ? clipValues[clipValues.length - 1]
              : 0,
        });
    } else {
      const value = getPreviousClipValue(clipValues, props.value);
      props.gelType === GEL_TYPE.DRAGGABLE &&
        onStripChange({
          colId,
          prevCellId,
          currentCellId: prevCellId,
          gelId,
          value,
        });
    }
  };

  const isUpIcon = () =>
    props.selected &&
    (cellType === CELL_TYPE.TRASH_CAN || cellType === CELL_TYPE.GEL_PATH);

  const isDownIcon = () =>
    props.selected &&
    (cellType === CELL_TYPE.GEL_HOLDER || cellType === CELL_TYPE.GEL_PATH);

  const onKeyUp = (event) => {
    if (props.options === null) {
      if (event.keyCode === KEY.DOWN_ARROW && props.options === null)
        onDownIconChange();
      if (event.keyCode === KEY.UP_ARROW && props.options === null)
        onUpIconChange();
    }
  };

  const onClick = () => {
    if (!props.options) {
      props.gelType === GEL_TYPE.DRAGGABLE && onGelBandClick(props.gelId);
    }
  };

  React.useEffect(() => {
    if (bandRef && bandRef.current && props.selected) {
      bandRef.current.focus();
    }
  }, [bandRef]);

  const setAriaLabel = () => {
    if (props.type === 'GEL_HOLDER') {
      return `Band [1] to be placed. Select the band to adjust the BP by using the arrow keys.`;
    } else if (props.type === 'GEL_PATH') {
      if (props.columnType === 'LADDER_COLUMN') {
        return `Band [1] at ${props.value}bp`;
      } else {
        return `Band [1] at ${props.value}bp. Select the band to adjust the BP by using the arrow keys.`;
      }
    } else if (props.type === 'TRASH_CAN') {
      return `Band [1] to be placed at trash. Select the band to adjust the BP by using the arrow keys.`;
    } else {
      return null;
    }
  };

  const setDropdownArialabel = () => {
    return `band at ${props.value}BP. Select the allele that matches the band from the list`;
  };

  return (
    <div
      style={style}
      ref={bandRef}
      onKeyUp={onKeyUp}
      onClick={onClick}
      onDragStart={onDragStart}
      draggable={setDraggable()}
      aria-label={setAriaLabel()}
      tabIndex={props.tabIndex}
      className={`${props.className}${props.gelType.toLowerCase()}`}>
      {props.options ? (
        <div className='outer band'>
          <div className='band_text' style={{position: 'absolute'}}>
            {props.children}
          </div>
          <div style={{position: 'absolute', marginTop: '15%'}}>
            <Dropdown
              value={props.selectedOption}
              ariaLabel={setDropdownArialabel()}
              className={'react-dropdown'}
              options={props.options.map((option) => {
                if (typeof option.label === 'string') {
                  option.label = parse(option.label);
                }
                return option;
              })}
              onChange={(e) => {
                const [colId, prevCellId, gelId] = splitIDs(props.gelId);
                onDropDownChange({
                  colId,
                  prevCellId,
                  currentCellId: prevCellId - 1,
                  gelId,
                  label: e.label,
                  value: e.value,
                });
              }}
              placeholder=''
            />
          </div>
        </div>
      ) : (
        <div className='band'>
          {isUpIcon() && (
            <IconUp className='band_iconup' onClick={onUpIconChange} />
          )}
          <div className='band_text'>{props.children}</div>
          {isDownIcon() && (
            <IconDown className='band_icondown' onClick={onDownIconChange} />
          )}
        </div>
      )}
    </div>
  );
}

GelBand.propTypes = {
  /**
   * Text to render inside GelStrip
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) || null,
  /**
   * Classname to be set on component
   */
  className: PropTypes.string,
  /**
   * Unique Id for Gel
   */
  gelId: PropTypes.string,
  /**
   * Type of Selected Option
   */
  selectedOption: PropTypes.object,
  /**
   * Type of Gel-Strip
   */
  gelType: PropTypes.oneOf(Object.keys(GEL_TYPE)),
  /**
   * Height value of Gel
   */
  height: PropTypes.number,
  /**
   * Opacity value of Gel
   */
  opacity: PropTypes.number,
  /**
   * Postion value of Gel
   */
  value: PropTypes.number || null,
  /**
   * Boolean to set if get is selected
   */
  selected: PropTypes.bool,
  /**
   * type is used to determine where is the gelband is displayed
   */
  type: PropTypes.string,
  /**
   * Tabindex value passed from outside the component for keyboard navigation
   */
  tabIndex: PropTypes.number,
  /**
   * ColumnType mention which typeof column
   */
  columnType: PropTypes.string,
};

GelBand.defaultProps = {
  selected: false,
  type: 'none',
  tabIndex: 0,
  options: null,
};
